import React, { useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import type { ComponentType } from 'react'
import ModalTitle from './modal/ModalTitle'
import ModalInner from './modal/ModalInner'
import ModalListWrapper from './modal/ModalListWrapper'
import ModalHeader from './modal/ModalHeader'
import ModalFooter from './modal/ModalFooter'
import ModalCloseButton from './modal/ModalCloseButton'
import modalStyles from './modal/modalStyles'

import BigOccupationList from './jobSelectModal/BigOccupationList'
import SearchControlBox from '@pc/components/Works/Panel/shared/SearchControlBox'
import PanelButtonPopupIcon from './PanelButtonPopupIcon'
import JobPanelButton from './jobSelectModal/JobPanelButton'
import useWorksCount from '@pc/components/Works/Panel/hook/useWorksCount'
import { buildDynamicSearchApiUrl } from '@pc/components/Works/Panel/util/workSearchApi'
import type { BigCategory, SearchParamsAttributes, SmallCategory } from '@pc/components/Works/Panel/type'

interface PropsType {
  params: SearchParamsAttributes
}

const isKey = <T extends object>(x: T, k: PropertyKey): k is keyof T => {
  return k in x
}

const ReactModal = Modal as unknown as ComponentType<any>

const el = document.querySelector<HTMLElement>('.js-works-search-panel')
if (el !== null) {
  Modal.setAppElement('.js-works-search-panel')
}

const JobSelectModal = (props: PropsType) => {
  const { params } = props
  const selectedJobCodes = params.job_codes ?? []
  const jobIds = params.job_type_ids ?? []

  const buildInitialJobsState = () => {
    const bigCategoriesAttr = el?.dataset.worksOccupationBigCategories ?? ''
    const smallCategoriesAttr = el?.dataset.worksOccupationSmallCategories ?? ''
    const bigCategories: BigCategory[] = JSON.parse(bigCategoriesAttr).data
    const smallCategories: SmallCategory[] = JSON.parse(smallCategoriesAttr).data

    const mappedSmallCategories = bigCategories.reduce((result, bigCategory) => {
      const value = smallCategories.filter((smallCategory) => smallCategory.attributes.occupation_big_category_id === bigCategory.attributes.id)
      return { ...result, [bigCategory.attributes.code]: value }
    }, {})

    return {
      bigCategories,
      mappedSmallCategories
    }
  }
  const { bigCategories, mappedSmallCategories } = buildInitialJobsState()
  const jobIdsByJobCodes = selectedJobCodes
    .map((jobCode: string) => {
      if (!isKey(mappedSmallCategories, jobCode)) return ''
      return (mappedSmallCategories[jobCode] as SmallCategory[]).map((smallCategory) => smallCategory.id)
    })
    .flat()
    .filter(Boolean)

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [selectedJobIds, setSelectedJobIds] = useState([...new Set([...jobIds, ...jobIdsByJobCodes])])
  const { worksCount, updateCountFromParams } = useWorksCount()
  const renderFlgRef = useRef(false)

  const toggleModal = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setModalIsOpen(!modalIsOpen)
  }

  const changeJobIds = (jobIds: string[]) => {
    setSelectedJobIds(jobIds)
  }

  const clearState = () => {
    setSelectedJobIds([])
  }

  const mergedParams = () => {
    // 検索パネルの仕様上、jobtagとjob_codeを保持していると検索結果がおかしくなってしまうためリセットしている
    return { ...params, job_type_ids: selectedJobIds, job_codes: [] }
  }

  useEffect(() => {
    if (renderFlgRef.current) {
      updateCountFromParams(mergedParams())
    } else {
      renderFlgRef.current = true
    }
  }, [selectedJobIds])

  return (
    <>
      <JobPanelButton onClick={toggleModal}>
        職種変更
        <PanelButtonPopupIcon />
      </JobPanelButton>
      <ReactModal isOpen={modalIsOpen} onRequestClose={toggleModal} style={modalStyles}>
        <ModalInner>
          <ModalCloseButton onClick={toggleModal} />
          <ModalHeader>
            <ModalTitle>職種を選択</ModalTitle>
          </ModalHeader>
          <ModalListWrapper>
            <BigOccupationList
              bigCategories={bigCategories}
              mappedSmallCategories={mappedSmallCategories}
              selectedJobIds={selectedJobIds}
              changeJobIds={changeJobIds}
            />
          </ModalListWrapper>
          <ModalFooter>
            <SearchControlBox searchUrl={buildDynamicSearchApiUrl(mergedParams())} onClickClearButton={clearState} worksCount={worksCount} />
          </ModalFooter>
        </ModalInner>
      </ReactModal>
    </>
  )
}

export default JobSelectModal
